import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Settings,
  Tag,
  Eye,
  EyeOff,
  Star,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { motion } from 'framer-motion';
import adminPricingService, { 
  PricingFeature, 
  PricingFeatureCreate, 
  TierWithFeatures 
} from '../../services/adminPricingService';

const PricingManagementPage: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<PricingFeature | null>(null);
  const [newFeature, setNewFeature] = useState<PricingFeatureCreate>({
    name: '',
    description: '',
    category: 'accounts',
    feature_type: 'limit',
    is_active: true,
    sort_order: 0
  });

  // Queries
  const { data: features, isLoading: featuresLoading } = useQuery({
    queryKey: ['pricing-features', selectedCategory],
    queryFn: () => adminPricingService.getPricingFeatures(
      selectedCategory === 'all' ? undefined : selectedCategory
    ),
  });

  const { data: categories } = useQuery({
    queryKey: ['feature-categories'],
    queryFn: adminPricingService.getFeatureCategories,
  });

  const { data: pricingPlans } = useQuery({
    queryKey: ['pricing-plans'],
    queryFn: adminPricingService.getPricingPlans,
  });

  // Mutations
  const createFeatureMutation = useMutation({
    mutationFn: adminPricingService.createPricingFeature,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-features'] });
      setIsCreateDialogOpen(false);
      setNewFeature({
        name: '',
        description: '',
        category: 'accounts',
        feature_type: 'limit',
        is_active: true,
        sort_order: 0
      });
      toast({ title: 'Success', description: 'Feature created successfully' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to create feature',
        variant: 'destructive'
      });
    },
  });

  const updateFeatureMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      adminPricingService.updatePricingFeature(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-features'] });
      setEditingFeature(null);
      toast({ title: 'Success', description: 'Feature updated successfully' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to update feature',
        variant: 'destructive'
      });
    },
  });

  const deleteFeatureMutation = useMutation({
    mutationFn: adminPricingService.deletePricingFeature,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-features'] });
      toast({ title: 'Success', description: 'Feature deleted successfully' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to delete feature',
        variant: 'destructive'
      });
    },
  });

  const handleCreateFeature = () => {
    createFeatureMutation.mutate(newFeature);
  };

  const handleUpdateFeature = (feature: PricingFeature, updates: any) => {
    updateFeatureMutation.mutate({ id: feature.id, data: updates });
  };

  const handleDeleteFeature = (featureId: string) => {
    if (window.confirm('Are you sure you want to delete this feature?')) {
      deleteFeatureMutation.mutate(featureId);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      ai: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200',
      content: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
      support: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
      storage: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200',
      analytics: 'bg-pink-100 text-pink-800 dark:bg-pink-900/50 dark:text-pink-200',
    };
    return colors[category] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-200';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-900 dark:via-slate-900 dark:to-gray-900">
      <div className="container mx-auto p-8 space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 dark:border-gray-700/50 shadow-lg"
        >
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              Pricing Plan Management
            </h1>
            <p className="text-slate-600 dark:text-gray-400 mt-2 text-lg">
              Configure features displayed on your pricing plans
            </p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add Feature
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create New Feature</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Feature Name</Label>
                  <Input
                    id="name"
                    value={newFeature.name}
                    onChange={(e) => setNewFeature({ ...newFeature, name: e.target.value })}
                    placeholder="e.g., AI Image Generation"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newFeature.description}
                    onChange={(e) => setNewFeature({ ...newFeature, description: e.target.value })}
                    placeholder="Describe what this feature includes..."
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select 
                    value={newFeature.category} 
                    onValueChange={(value) => setNewFeature({ ...newFeature, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="accounts">Account Limits</SelectItem>
                      <SelectItem value="ai">AI Features</SelectItem>
                      <SelectItem value="content">Content Management</SelectItem>
                      <SelectItem value="support">Support</SelectItem>
                      <SelectItem value="storage">Storage</SelectItem>
                      <SelectItem value="analytics">Analytics</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="feature_type">Feature Type</Label>
                  <Select 
                    value={newFeature.feature_type} 
                    onValueChange={(value: any) => setNewFeature({ ...newFeature, feature_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="boolean">Yes/No Feature</SelectItem>
                      <SelectItem value="limit">Numeric Limit</SelectItem>
                      <SelectItem value="text">Custom Text</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={newFeature.is_active}
                    onCheckedChange={(checked) => setNewFeature({ ...newFeature, is_active: checked })}
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
                <Button 
                  onClick={handleCreateFeature} 
                  disabled={createFeatureMutation.isPending}
                  className="w-full"
                >
                  {createFeatureMutation.isPending ? 'Creating...' : 'Create Feature'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center space-x-4 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-xl p-4 border border-white/20 dark:border-gray-700/50"
        >
          <Label>Filter by Category:</Label>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories?.map((category) => (
                <SelectItem key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </motion.div>

        {/* Features List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-0 shadow-xl rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Settings className="w-5 h-5" />
                <span>Pricing Features</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {featuresLoading ? (
                <div className="text-center py-8">Loading features...</div>
              ) : (
                <div className="space-y-4">
                  {features?.map((feature) => (
                    <motion.div
                      key={feature.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center justify-between p-4 border border-slate-200 dark:border-gray-600 rounded-xl hover:shadow-md transition-all"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-semibold text-lg">{feature.name}</h3>
                          <Badge className={getCategoryColor(feature.category)}>
                            {feature.category}
                          </Badge>
                          <Badge variant={feature.feature_type === 'boolean' ? 'default' : 'secondary'}>
                            {feature.feature_type}
                          </Badge>
                          {!feature.is_active && (
                            <Badge variant="destructive">
                              <EyeOff className="w-3 h-3 mr-1" />
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <p className="text-slate-600 dark:text-gray-400">{feature.description}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUpdateFeature(feature, { is_active: !feature.is_active })}
                        >
                          {feature.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingFeature(feature)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteFeature(feature.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Pricing Plans Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-0 shadow-xl rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Tag className="w-5 h-5" />
                <span>Pricing Plans Preview</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pricingPlans?.tiers.map((tier) => (
                  <div
                    key={tier.id}
                    className="border border-slate-200 dark:border-gray-600 rounded-xl p-6 hover:shadow-lg transition-all"
                  >
                    <div className="text-center mb-4">
                      <h3 className="text-xl font-bold">{tier.display_name}</h3>
                      <div className="text-2xl font-bold text-indigo-600 mt-2">
                        ${tier.price_monthly || 0}/mo
                      </div>
                    </div>
                    <div className="space-y-2">
                      {tier.features.map((feature) => (
                        <div
                          key={feature.id}
                          className={`flex items-center space-x-2 text-sm ${
                            feature.is_highlighted ? 'font-semibold text-indigo-600' : ''
                          }`}
                        >
                          {feature.is_included ? (
                            <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                              <div className="w-2 h-2 bg-white rounded-full" />
                            </div>
                          ) : (
                            <X className="w-4 h-4 text-red-500" />
                          )}
                          <span>
                            {feature.display_text || feature.pricing_feature.name}
                          </span>
                          {feature.is_highlighted && <Star className="w-3 h-3 text-yellow-500" />}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingManagementPage;